<script>
  import { onMount } from 'svelte';
  import { fly } from 'svelte/transition';
  import Log from '@utils/log';
  import Track from '@utils/track';
  import { inview } from 'svelte-inview';

  import SuggestedFollowAjax from '@ajax/suggested_follow';

  import Avatar from '@components/Avatar.svelte';
  import UserFollowButton from '@app-components/UserFollowButton.svelte';

  export let currentUser;
  export let mobile = false;
  export let category;
  export let maxShown = 4;
  export let page = 'discover';

  let minShown = 1;

  export let mobilePaddedLeft = 0;
  export let view;
  export let suggestions = [];

  let connections = {};
  let dismissedSuggestionIds = [];
  let initialViewEventsSent = false;

  const totalSuggestions = maxShown + 4;

  let shownSuggestions = suggestions.slice(0, maxShown);

  let loadSuggestions = async (keepCurrent = false) => {
    let response;
    let excludeIds = [...dismissedSuggestionIds, ...suggestions.map((s) => s.id)];

    if (
      keepCurrent == false &&
      window.suggestedFollowersSuggestions &&
      window.suggestedFollowersSuggestions.length > 0 &&
      window.suggestedFollowersCategory == category
    ) {
      suggestions = window.suggestedFollowersSuggestions;
      connections = window.suggestedFollowersConnections;
      shownSuggestions = suggestions.slice(0, maxShown);

      if (dismissedSuggestionIds.length == 0) {
        dismissedSuggestionIds = window.suggestedFollowersDismissedSuggestionIds || [];
      }

      return;
    }

    if (category) {
      if (currentUser && currentUser.id) {
        response = await SuggestedFollowAjax.category({
          category: category,
          exclude_ids: excludeIds,
          total: totalSuggestions,
        });
      } else {
        response = await SuggestedFollowAjax.curated_category({
          category: category,
          exclude_ids: excludeIds,
          total: totalSuggestions,
        });
      }
    } else {
      response = await SuggestedFollowAjax.network({ total: totalSuggestions, exclude_ids: excludeIds });
    }

    if (!keepCurrent) {
      suggestions = [];
    }

    if (response && response.users && response.users.length > 0) {
      response.users.forEach((user) => {
        if (!suggestions.find((s) => s.id == user.id)) {
          suggestions.push(user);
        }
      });

      if (response.connections) {
        connections = { ...connections, ...response.connections };
      }
    }

    window.suggestedFollowersSuggestions = suggestions;
    window.suggestedFollowersConnections = connections;
    window.suggestedFollowersCategory = category;
    shownSuggestions = suggestions.slice(0, maxShown);
  };

  let dismissSuggestion = function (suggestion) {
    dismissedSuggestionIds.push(suggestion.id);
    suggestions = suggestions.filter((s) => s.id != suggestion.id);
    shownSuggestions = suggestions.slice(0, maxShown);

    SuggestedFollowAjax.ignore({ ignore_user: suggestion.id });

    window.suggestedFollowersDismissedSuggestionIds = dismissedSuggestionIds;
    window.suggestedFollowersSuggestions = suggestions;

    Track.suggestedFollowDismissed({ currentUser, relatedUserId: suggestion.id, page });

    if (suggestions.length <= maxShown + 2) {
      loadSuggestions(true);
    }
  };

  let afterFollow = function (user) {
    let page = view ? view : category || 'trending';

    Log.info(
      `[FOLLOW] | current user: ${currentUser && currentUser.name} (${
        currentUser && currentUser.id
      }) | followed user: ${user.name} (${user.id}) | page: ${page}`,
      false,
      '#suggested-followers'
    );

    setTimeout(() => {
      dismissedSuggestionIds.push(user.id);
      suggestions = suggestions.filter((s) => s.id != user.id);
      shownSuggestions = suggestions.slice(0, maxShown);

      if (suggestions.length <= maxShown + 2) {
        loadSuggestions(true);
      }
    }, 750);

    Track.suggestedFollowFollowed({ currentUser, relatedUserId: user.id, page });
  };

  let afterUnfollow = function (user) {
    Log.info(
      `[UNFOLLOW] | current user: ${currentUser && currentUser.name} (${
        currentUser && currentUser.id
      }) | unfollowed user: ${user.name} (${user.id})`,
      false,
      '#suggested-followers'
    );
  };

  let endOfSuggestionsReached = () => {
    if (initialViewEventsSent) {
      return;
    }

    shownSuggestions.forEach((suggestion) => {
      Track.suggestedFollowViewed({ currentUser, relatedUserId: suggestion.id, page });
    });

    initialViewEventsSent = true;
  };

  onMount(() => {
    loadSuggestions(false);
  });
</script>

<style style="text/scss" lang="scss">
  .suggested-followers-container {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
    gap: 6px;
    flex: 1;

    .suggested-follower {
      border-radius: 15px;
      padding: 12px;
      min-width: 238px;
      max-width: 350px;
      width: 100%;
      position: relative;

      &:hover {
        background-color: #f5f5f5;
        cursor: pointer;

        .suggested-follower-close {
          display: block;
        }
      }

      .suggested-follower-close {
        position: absolute;
        top: 6px;
        right: 6px;
        cursor: pointer;
        display: none;
        padding: 10px;
        opacity: 0.5;

        &:hover {
          opacity: 1;
        }
      }

      .suggested-follower-image {
        margin-top: 8px;
        display: flex;
        justify-content: center;
        margin-bottom: 12px;
      }

      .suggested-follower-name {
        font-size: 16px;
        font-weight: 500;
        color: #1a1a1a;
        margin-bottom: 10px;
        text-align: center;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      .suggested-follower-description {
        font-size: 13px;
        font-weight: 400;
        line-height: 16px;
        color: #7f7f7f;
        margin-bottom: 6px;
        justify-content: center;
        display: flex;

        b {
          color: #1a1a1a;
          font-weight: 500;
        }

        .suggested-follower-connection {
          display: flex;
          align-items: center;
          overflow: hidden;

          img {
            width: 20px;
            height: 20px;
            border-radius: 999px;
          }

          span {
            margin-left: 6px;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
          }
        }

        .suggested-follower-new-trending {
          display: flex;
          align-items: center;

          img {
            width: 16px;
            height: 16px;
            margin-right: 6px;
          }
        }

        .suggested-follower-top-category {
          display: flex;
          align-items: center;
          overflow: hidden;
          height: 18px;

          span {
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
          }

          img {
            width: 15px;
            height: 15px;
            margin-left: 6px;
          }
        }
      }
    }
  }

  .suggested-for-you {
    color: #1a1a1a;
    font-weight: 500;
    font-size: 17px;
    line-height: 26px;
    display: none;
  }

  .suggested-for-you.mobile {
    display: block;
    margin-left: 15px;
  }

  .suggested-followers-container.mobile {
    overflow: auto;
    background: transparent;
    box-shadow: none;
    padding-top: 40px;
    padding-bottom: 40px;
    margin-top: -20px;
    margin-bottom: -20px;
    gap: 19px;
    margin-left: 0px;
    border-radius: 0px;
    justify-content: flex-start;

    .suggested-follower {
      padding: 14px;
      padding-left: 14px;
      padding-right: 14px;
      margin: 0px;
      background: white;
      box-shadow: 0px 12px 23px 0px rgba(0, 0, 0, 0.09);

      &:hover {
        background-color: transparent;
      }

      .suggested-follower-close {
        display: block;
      }
    }
  }

  @media (min-width: 700px) {
    .suggested-followers-container.mobile {
      .suggested-follower {
        max-width: inherit;
      }
    }
  }

  // followings

  .suggested-for-you.followings:not(.mobile) {
    display: block;
    margin-top: 30px;
    padding-bottom: 16px;
    zoom: 0.9;
    text-align: left;
  }

  .suggested-followers-container.followings:not(.mobile) {
    margin-bottom: 38px;
    zoom: 0.9;
    gap: 14px;

    .suggested-follower {
      border: 4px solid #f5f5f5;
    }
  }

  .suggested-for-you.followings.mobile {
    margin-top: 28px;
    padding-bottom: 0px;
    zoom: 0.88;
    text-align: left;
  }

  .suggested-followers-container.followings.mobile {
    margin-bottom: 4px;
    zoom: 0.88;

    .suggested-follower {
      box-shadow: none;
      border: 4px solid #f5f5f5;
    }
  }
</style>

{#if shownSuggestions.length >= minShown}
  <div
    class="suggested-for-you {view} {mobile ? 'mobile' : ''}"
    style={mobile && mobilePaddedLeft > 0 ? `padding-left: ${mobilePaddedLeft}px` : ''}>
    Suggested for you
  </div>
  <div
    class="suggested-followers-container {view} {mobile ? 'mobile no-scrollbar' : 'desktop'}"
    style={mobile && mobilePaddedLeft > 0 ? `padding-left: ${mobilePaddedLeft}px` : ''}>
    {#each shownSuggestions as suggestion (suggestion.id)}
      <a
        in:fly={{ duration: 400, x: 20 }}
        href={suggestion.profile_path}
        on:click|preventDefault|stopPropagation={() => {
          Track.suggestedFollowClicked({ currentUser, relatedUserId: suggestion.id, page });
          window.open(suggestion.profile_path, '_blank');
        }}
        class="suggested-follower">
        {#if currentUser && currentUser.id}
          <div
            class="suggested-follower-close"
            on:click|preventDefault|stopPropagation={() => dismissSuggestion(suggestion)}>
            <img src="/images/suggested-follows/close.svg" />
          </div>
        {/if}
        <div class="suggested-follower-image">
          <Avatar width={56} src={suggestion.avatar_url} user={suggestion} mobile={mobile} />
        </div>
        <div class="suggested-follower-name">
          {suggestion.name}
        </div>
        <div class="suggested-follower-description">
          {#if suggestion.ui_category && suggestion.ui_category !== 'Trending'}
            <div class="suggested-follower-top-category">
              <span>Top curator for <b>{suggestion.ui_category}</b></span>

              {#if suggestion.ui_category_data && suggestion.ui_category_data.image}
                <div>
                  <img src={suggestion.ui_category_data.image} />
                </div>
              {/if}
            </div>
          {:else if suggestion.connection_id && connections[suggestion.connection_id]}
            <div class="suggested-follower-connection">
              <img src={connections[suggestion.connection_id]['avatar_url']} />
              <span
                >Followed by <b>{connections[suggestion.connection_id]['first_name']}</b>
                {#if suggestion.n_connections - 1 > 0}
                  and <b>{suggestion.n_connections - 1} more</b>
                {/if}
              </span>
            </div>
          {:else}
            <div class="suggested-follower-new-trending">
              <img src="/images/suggested-follows/new-trending.svg" />
              <span>New and trending</span>
            </div>
          {/if}
        </div>
        <div class="suggested-follower-follow-button">
          <UserFollowButton
            user={suggestion}
            size="small"
            afterFollow={afterFollow}
            afterUnfollow={afterUnfollow}
            context="suggested_follow" />
        </div>
      </a>
    {/each}
  </div>
  <div use:inview={{}} on:enter={endOfSuggestionsReached} />
{/if}
